import React, { useMemo } from 'react';
import { __ } from '@wordpress/i18n';

// components
import CueTag from '../CueTag';

// icons
import CaretIcon from '../../../../icons/CaretIcon';
import CircleCheckIcon from '../../../../icons/CircleCheckIcon';
import PencilIcon from '../../../../icons/PencilIcon';
import TaskAssignedIcon from '../../../../icons/TaskAssignedIcon';

// store
import { useCommCenter } from '../../context';

const memberName = (member) => [member?.name, member?.surname].filter(Boolean).join(' ')
    || member?.invitedEmail
    || '';

/**
 * Draft activity generated from a comment. The backend returns it with status
 * `draft`; "Modifica" edits the body and the assignee before it is confirmed.
 *
 * Every control here is role gated, the same way `capabilities.py` is:
 * a producer or a director assigns anybody and edits any draft, a musician only
 * takes the task themselves and only edits the drafts they generated, and a
 * customer — who cannot generate one in the first place — just reads it.
 */
const ActivityDraft = ({ comment }) => {
    const { draft } = comment;
    const {
        team,
        can,
        pending,
        updateDraft,
        cancelDraft,
        confirmDraft,
        openCue,
    } = useCommCenter();

    // Only accepted members can carry a task, and only the ones this role may
    // hand it to are offered.
    const assignable = useMemo(() => can.assignableMembers(team), [can, team]);

    const canEdit = can.editDraft(draft);
    const canConfirm = can.confirmDraft && assignable.length > 0;

    const assignee = assignable.find(m => m.userId === draft.assigneeId) || assignable[0] || null;
    const assigneeLabel = assignee ? memberName(assignee) : __('Nessuno', 'soundbetween');

    const assigneeSelect = assignable.length > 1 && canEdit
        ? (
            <span className="activityDraft__assignee">
                <select
                    value={draft.assigneeId || ''}
                    onChange={(e) => updateDraft(comment.id, { assigneeId: e.target.value })}
                    aria-label={__('Scegli a chi assegnare', 'soundbetween')}>
                    {assignable.map(member => (
                        <option key={member.userId} value={member.userId}>{memberName(member)}</option>
                    ))}
                </select>
                <span className="activityDraft__assigneeLabel">
                    {assigneeLabel.split(' ')[0]}
                    <CaretIcon/>
                </span>
            </span>
        )
        : <strong className="richText__actor">{assigneeLabel.split(' ')[0]}</strong>;

    return (
        <div className="activityDraft">
            {draft.editing && canEdit
                ? (
                    <div className="activityDraft__editor">
                        {assigneeSelect}
                        <textarea
                            value={draft.text}
                            onChange={(e) => updateDraft(comment.id, { text: e.target.value })}
                            aria-label={__('Messaggio dell’attività', 'soundbetween')}/>
                    </div>
                )
                : (
                    <div className="activityDraft__card">
                        <span className="activityDraft__badge">
                            <TaskAssignedIcon size={14}/>
                            {draft.name || __('Attività assegnata', 'soundbetween')}
                        </span>
                        <p className="activityDraft__text">
                            {assigneeSelect}
                            {' '}
                            {draft.cue
                                ? (
                                    <>
                                        {draft.text.replace(draft.cue.code, '').trim()}
                                        {' '}
                                        <CueTag
                                            code={draft.cue.code}
                                            instrument={draft.cue.instrument}
                                            onClick={() => openCue(draft.cue)}/>
                                    </>
                                )
                                : draft.text}
                        </p>
                        <span className="activityDraft__time">{__('Adesso', 'soundbetween')}</span>
                    </div>
                )}

            <div className="activityDraft__actions">
                {canEdit && (
                    <button
                        type="button"
                        className="commButton commButton--ghost"
                        onClick={() => updateDraft(comment.id, { editing: !draft.editing })}>
                        {draft.editing
                            ? <><CircleCheckIcon/>{__('Modifica completata', 'soundbetween')}</>
                            : <><PencilIcon/>{__('Modifica', 'soundbetween')}</>}
                    </button>
                )}
                {canConfirm && (
                    <button
                        type="button"
                        className="commButton commButton--accent"
                        disabled={pending?.confirm || !assignee}
                        onClick={() => confirmDraft(comment.id, assignee?.userId)}>
                        {pending?.confirm
                            ? __('Assegno…', 'soundbetween')
                            : can.assignAnyone
                                ? __('Conferma assegnazione', 'soundbetween')
                                : __('Prendi in carico', 'soundbetween')}
                    </button>
                )}
                <button
                    type="button"
                    className="commButton commButton--quiet"
                    onClick={() => cancelDraft(comment.id)}>
                    {canEdit || canConfirm
                        ? __('Annulla', 'soundbetween')
                        : __('Nascondi', 'soundbetween')}
                </button>
            </div>
        </div>
    )
}

export default ActivityDraft;
